<p-toast></p-toast>

<div class="devadmin-container">
  <div class="devadmin-header">
    <h1>Dev Admin - SOAP Tuning</h1>
    <button pButton type="button" label="Reset" class="p-button-secondary" (click)="resetAll()"></button>
  </div>

  <div class="panels">
    <p-card header="Run Analysis Preview" class="panel">
      <div class="form-row">
        <label for="specialty">Specialty</label>
        <input id="specialty" pInputText [(ngModel)]="specialty" />
      </div>

      <div class="form-row">
        <label for="transcriptText">Transcript</label>
        <textarea
          id="transcriptText"
          pInputTextarea
          [(ngModel)]="transcriptText"
          rows="10"
        ></textarea>
      </div>

      <button pButton type="button" label="Run Analysis" (click)="runAnalysis()"></button>

      <div class="loading-block" *ngIf="analysisLoading">
        <p-progressSpinner></p-progressSpinner>
        <p>Running analysis...</p>
      </div>

    </p-card>

    <p-card header="Auto Replay Range (Analyze + Compare)" class="panel">
      <div class="form-row">
        <label for="replayVersion">Analysis Version</label>
        <select id="replayVersion" class="p-inputtext" [(ngModel)]="replayVersion">
          <option value="v2">v2</option>
          <option value="v3">v3</option>
        </select>
      </div>

      <div class="form-row">
        <label for="replayStartId">Start Transcription ID</label>
        <input id="replayStartId" pInputText [(ngModel)]="replayStartId" />
      </div>

      <div class="form-row">
        <label for="replayEndId">End Transcription ID</label>
        <input id="replayEndId" pInputText [(ngModel)]="replayEndId" />
      </div>

      <button pButton type="button" label="Replay Range" (click)="runReplayRange()"></button>

      <table class="details-table" *ngIf="replayState?.availableCount">
        <tbody>
          <tr>
            <th>Desired SOAP Available</th>
            <td>
              {{ replayState?.availableMinId }} → {{ replayState?.availableMaxId }}
              ({{ replayState?.availableCount }} transcription IDs)
            </td>
          </tr>
        </tbody>
      </table>

      <table class="details-table" *ngIf="replayState?.lastRunId">
        <tbody>
          <tr>
            <th>Last Run</th>
            <td>{{ replayState?.lastStartId }} → {{ replayState?.lastEndId }} ({{ replayState?.lastRunAt }})</td>
          </tr>
          <tr *ngIf="replayState?.suggestedNextStartId">
            <th>Suggested Next Start</th>
            <td>{{ replayState?.suggestedNextStartId }}</td>
          </tr>
        </tbody>
      </table>

      <div class="loading-block" *ngIf="replayLoading">
        <p-progressSpinner></p-progressSpinner>
        <p>Replaying range...</p>
      </div>

      <details class="transcript-details" *ngIf="replayRuns?.length">
        <summary>Replay History</summary>
        <table class="history-table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Range</th>
              <th>Processed</th>
              <th>Skipped</th>
              <th>Failed</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let run of replayRuns.slice().reverse()">
              <td>{{ run.runId }}</td>
              <td>{{ run.startId }} → {{ run.endId }}</td>
              <td>{{ run.processedCount ?? '-' }}</td>
              <td>{{ run.skippedCount ?? '-' }}</td>
              <td>{{ run.failedCount ?? '-' }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </p-card>

    <p-card header="Compare Desired SOAP" class="panel">
      <div class="form-row">
        <label for="transcriptionId">Transcription ID</label>
        <input id="transcriptionId" pInputText [(ngModel)]="transcriptionId" />
      </div>
      <div class="compare-actions">
        <button pButton type="button" label="Use" (click)="useTranscriptionId()"></button>
        <button pButton type="button" label="Reanalyze" class="p-button-secondary" (click)="reanalyzeByTranscriptionId()"></button>
      </div>

      <div class="form-row">
        <label for="desiredSoap">Desired SOAP Output</label>
        <textarea
          id="desiredSoap"
          pInputTextarea
          [(ngModel)]="desiredSoapText"
          rows="10"
        ></textarea>
      </div>

      <button pButton type="button" label="Compare" (click)="compareSoap()"></button>

      <div class="loading-block" *ngIf="compareLoading">
        <p-progressSpinner></p-progressSpinner>
        <p>Comparing...</p>
      </div>
    </p-card>
  </div>

  <p-card header="Analysis Output" class="panel result-panel" *ngIf="analysisResult">
    <div class="result-block">
      <h3>Overview</h3>
      <table class="details-table">
        <tbody>
          <tr>
            <th>Status</th>
            <td>{{ analysisResult.status || 'Unknown' }}</td>
          </tr>
          <tr *ngIf="analysisResult.transcriptionId">
            <th>Transcription ID</th>
            <td>{{ analysisResult.transcriptionId }}</td>
          </tr>
          <tr *ngIf="analysisResult.promptVersion">
            <th>Prompt Version</th>
            <td>{{ analysisResult.promptVersion }}</td>
          </tr>
          <tr *ngIf="analysisResult.summary">
            <th>Summary</th>
            <td>{{ analysisResult.summary }}</td>
          </tr>
          <tr *ngIf="analysisResult.diagnosis">
            <th>Diagnosis</th>
            <td>{{ analysisResult.diagnosis }}</td>
          </tr>
        </tbody>
      </table>

      <h3>SOAP Note</h3>
      <div class="soap-section">
        <strong>Subjective</strong>
        <pre>{{ (analysisResult.soapNote?.subjective || '') | bulletizeNumberedHeadings | soapNormalize:'subjective' }}</pre>
      </div>
      <div class="soap-section">
        <strong>Objective</strong>
        <pre>{{ (analysisResult.soapNote?.objective || '') | bulletizeNumberedHeadings | soapNormalize:'objective' }}</pre>
      </div>
      <div class="soap-section">
        <strong>Assessment</strong>
        <pre>{{ (analysisResult.soapNote?.assessment || '') | bulletizeNumberedHeadings }}</pre>
      </div>
      <div class="soap-section">
        <strong>Plan</strong>
        <pre>{{ (analysisResult.soapNote?.plan || '') | bulletizeNumberedHeadings }}</pre>
      </div>

      <h3>AI Analysis</h3>
      <div class="ai-section">
        <strong>Key Findings</strong>
        <ul>
          <li *ngFor="let item of analysisResult.aiAnalysis?.keyFindings || []">{{ item }}</li>
        </ul>
      </div>
      <div class="ai-section">
        <strong>Treatment Plan Summary</strong>
        <ul>
          <li *ngFor="let item of analysisResult.aiAnalysis?.treatmentPlanSummary || []">{{ item }}</li>
        </ul>
      </div>
      <div class="ai-section">
        <strong>Suggested ICD-10 Codes</strong>
        <ul>
          <li *ngFor="let item of analysisResult.aiAnalysis?.suggestedICD10Codes || []">
            {{ item.code }} — {{ item.description }}
          </li>
        </ul>
        <p *ngIf="!(analysisResult.aiAnalysis?.suggestedICD10Codes || []).length">No suggested ICD-10 codes.</p>
      </div>
      <div class="ai-section">
        <strong>Medical Terms</strong>
        <ul>
          <li *ngFor="let item of analysisResult.aiAnalysis?.medicalTerms || []">
            {{ item.term }} — {{ item.definition }}
          </li>
        </ul>
        <p *ngIf="!(analysisResult.aiAnalysis?.medicalTerms || []).length">No medical terms.</p>
      </div>

      <h3>Extracted Medical Codes</h3>
      <div class="ai-section">
        <strong>ICD-10</strong>
        <ul>
          <li *ngFor="let item of analysisResult.medicalCodes?.icd10Codes || []">
            {{ item.code }} — {{ item.text }}
          </li>
        </ul>
        <p *ngIf="!(analysisResult.medicalCodes?.icd10Codes || []).length">No ICD-10 codes detected.</p>
      </div>
      <div class="ai-section">
        <strong>RxNorm</strong>
        <ul>
          <li *ngFor="let item of analysisResult.medicalCodes?.rxNormCodes || []">
            {{ item.code }} — {{ item.text }}
          </li>
        </ul>
        <p *ngIf="!(analysisResult.medicalCodes?.rxNormCodes || []).length">No RxNorm codes detected.</p>
      </div>
      <div class="ai-section">
        <strong>SNOMED</strong>
        <ul>
          <li *ngFor="let item of analysisResult.medicalCodes?.snomedCodes || []">
            {{ item.code }} — {{ item.text }}
          </li>
        </ul>
        <p *ngIf="!(analysisResult.medicalCodes?.snomedCodes || []).length">No SNOMED codes detected.</p>
      </div>

      <details class="transcript-details">
        <summary>Original Transcript</summary>
        <pre>{{ analysisResult.transcriptText || '' }}</pre>
      </details>
    </div>
  </p-card>

  <p-card header="Comparison Output" class="panel result-panel" *ngIf="compareResult">
    <div class="result-block">
      <h3>Overview</h3>
      <table class="details-table">
        <tbody>
          <tr>
            <th>Overall Similarity</th>
            <td>{{ compareResult.overallSimilarityScore | number:'1.0-2' }}</td>
          </tr>
          <tr *ngIf="compareResult.promptVersion">
            <th>Prompt Version</th>
            <td>{{ compareResult.promptVersion }}</td>
          </tr>
          <tr *ngIf="compareResult.possibleImprovement">
            <th>Possible Improvement</th>
            <td>{{ compareResult.possibleImprovement }}</td>
          </tr>
          <tr *ngIf="compareResult.learnedSummary">
            <th>Learned Summary</th>
            <td>{{ compareResult.learnedSummary }}</td>
          </tr>
        </tbody>
      </table>

      <h3>Section Comparison</h3>
      <div class="compare-section" *ngFor="let section of compareResult.sectionComparisons">
        <strong>{{ section.section | titlecase }}</strong>
        <p>Similarity: {{ section.similarityScore | number:'1.0-2' }}</p>
        <div>
          <em>Missing items</em>
          <ul>
            <li *ngFor="let item of section.missingItems">{{ item }}</li>
          </ul>
        </div>
        <div>
          <em>Extra items</em>
          <ul>
            <li *ngFor="let item of section.extraItems">{{ item }}</li>
          </ul>
        </div>
      </div>
    </div>
  </p-card>

  <p-card header="Replay Range Output" class="panel result-panel" *ngIf="replayResult">
    <div class="result-block">
      <h3>Overview</h3>
      <table class="details-table">
        <tbody>
          <tr>
            <th>Run ID</th>
            <td>{{ replayResult.runId }}</td>
          </tr>
          <tr *ngIf="replayResult.analysisVersion">
            <th>Analysis Version</th>
            <td>{{ replayResult.analysisVersion }}</td>
          </tr>
          <tr>
            <th>Range</th>
            <td>{{ replayResult.startId }} → {{ replayResult.endId }}</td>
          </tr>
          <tr>
            <th>Counts</th>
            <td>
              Processed: {{ replayResult.processedCount }},
              Skipped: {{ replayResult.skippedCount }},
              Failed: {{ replayResult.failedCount }}
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Results (Old Analysis ID → New Analysis ID)</h3>
      <table class="history-table">
        <thead>
          <tr>
            <th>Transcription ID</th>
            <th>Status</th>
            <th>Message</th>
            <th>Old Analysis ID</th>
            <th>New Analysis ID</th>
            <th>Similarity</th>
            <th>Comparison ID</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of replayResult.items">
            <td>{{ item.transcriptionId }}</td>
            <td>{{ item.status }}</td>
            <td>{{ item.message || '-' }}</td>
            <td>{{ item.oldAnalysisId || '-' }}</td>
            <td>{{ item.newAnalysisId || '-' }}</td>
            <td>
              {{ item.overallSimilarityScore == null ? '-' : (item.overallSimilarityScore | number:'1.0-2') }}
            </td>
            <td>{{ item.comparisonId || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </p-card>
</div>
